'use client';

import { useMemo } from 'react';
import { useT } from '@/components/i18n-provider';
import { LocateFixed, Shapes, Spline, Square, StickyNote, Type } from 'lucide-react';
import {
  useViewport,
  useStore,
  useStoreApi,
  type ReactFlowState,
} from '@xyflow/react';
import {
  usePendingAnnotation,
  useWorkspaceActions,
} from '@/components/workspace/store';
import { RailFlyout, FlyoutRow } from './rail-flyout';

/**
 * Canvas controls for the rail (V2): the annotation **Tools** flyout and the
 * "Back to content" pill. They replace the floating `ZoomControls` column,
 * which is gated off under railV2 in `workspace-canvas.tsx`. Both live inside
 * the ReactFlow + workspace-store providers, so they drive the same
 * `pendingAnnotation` slice and camera the legacy column used. There is no
 * zoom control here: workspace zoom is disabled (see `WORKSPACE_ZOOM_ENABLED`).
 */

type ToolPick =
  | { kind: 'sticky' | 'text' }
  | { kind: 'shape'; shape?: 'rectangle' | 'ellipse' | 'diamond' | 'triangle' }
  | { kind: 'connector' };

export function RailTools() {
  const t = useT();
  const pending = usePendingAnnotation();
  const { setPendingAnnotation } = useWorkspaceActions();

  // Re-selecting the armed tool toggles it off (mirrors the legacy
  // ZoomControls strip). The canvas pane-click handler consumes
  // `pendingAnnotation` and drops the matching widget.
  const pickTool = (pick: ToolPick) => {
    if (
      pending &&
      pending.kind === pick.kind &&
      ('shape' in pending ? pending.shape : undefined) ===
        ('shape' in pick ? pick.shape : undefined)
    ) {
      setPendingAnnotation(null);
      return;
    }
    if (pick.kind === 'shape') {
      setPendingAnnotation({ kind: 'shape', shape: pick.shape ?? 'rectangle' });
    } else if (pick.kind === 'sticky' || pick.kind === 'text') {
      setPendingAnnotation({ kind: pick.kind });
    } else {
      setPendingAnnotation({ kind: 'connector' });
    }
  };

  return (
    <RailFlyout
      icon={Shapes}
      label={t('agents.tools')}
      active={!!pending}
      render={(close) => (
        <>
          <FlyoutRow
            icon={StickyNote}
            label={t('workspace.zoom.stickyNote')}
            active={pending?.kind === 'sticky'}
            onClick={() => {
              pickTool({ kind: 'sticky' });
              close();
            }}
          />
          <FlyoutRow
            icon={Square}
            label={t('workspace.zoom.shape')}
            active={pending?.kind === 'shape'}
            onClick={() => {
              pickTool({ kind: 'shape' });
              close();
            }}
          />
          <FlyoutRow
            icon={Type}
            label={t('workspace.zoom.text')}
            active={pending?.kind === 'text'}
            onClick={() => {
              pickTool({ kind: 'text' });
              close();
            }}
          />
          <FlyoutRow
            icon={Spline}
            label={t('workspace.zoom.connector')}
            active={pending?.kind === 'connector'}
            onClick={() => {
              pickTool({ kind: 'connector' });
              close();
            }}
          />
        </>
      )}
    />
  );
}

/**
 * Content bounding box of all nodes, as a rounded string key so the selector
 * only re-renders when the box actually changes (not on every store tick).
 * Empty string = no content on the canvas.
 */
function selectContentBboxKey(s: ReactFlowState): string {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let count = 0;
  s.nodeLookup.forEach((n) => {
    const pos = n.internals?.positionAbsolute ?? n.position;
    const w = n.measured?.width ?? 0;
    const h = n.measured?.height ?? 0;
    count += 1;
    if (pos.x < minX) minX = pos.x;
    if (pos.y < minY) minY = pos.y;
    if (pos.x + w > maxX) maxX = pos.x + w;
    if (pos.y + h > maxY) maxY = pos.y + h;
  });
  if (!count) return '';
  return `${Math.round(minX)},${Math.round(minY)},${Math.round(maxX)},${Math.round(maxY)}`;
}

const selectPaneSizeKey = (s: ReactFlowState): string =>
  `${Math.round(s.width)}x${Math.round(s.height)}`;

/**
 * Canvas recenter affordance (rail-redesign P6). NOT a persistent FAB: it only
 * appears once the viewport has been panned so far that NO content is visible
 * ("nothing-land"). Because it only shows when the canvas area is empty of
 * widgets, it can never visually collide with an app.
 *
 * Click = go back to the item nearest the middle of the view, through the
 * canvas's `ws:focus-item` chokepoint: a window is framed at 100% the way the
 * taskbar frames it, anything else is centred at the current zoom. It used to
 * be "Fit to content", a `fitView` over every node, which zoomed although
 * workspace zoom is disabled, and on a workspace of stacked screens it centred
 * the camera on the EMPTY screen between them (the bounding-box middle), so
 * the pill landed you somewhere with nothing to see (found 2026-09-26).
 */
export function BackToContentPill() {
  const t = useT();
  const store = useStoreApi();
  const { x, y, zoom } = useViewport();
  const bboxKey = useStore(selectContentBboxKey);
  const sizeKey = useStore(selectPaneSizeKey);

  const offContent = useMemo(() => {
    if (!bboxKey) return false; // empty canvas → nothing to recenter to
    const [minX, minY, maxX, maxY] = bboxKey.split(',').map(Number);
    const [w, h] = sizeKey.split('x').map(Number);
    if (!w || !h || !zoom) return false;
    // Visible region in flow coordinates.
    const vx0 = -x / zoom;
    const vy0 = -y / zoom;
    const vx1 = vx0 + w / zoom;
    const vy1 = vy0 + h / zoom;
    const intersects = minX < vx1 && maxX > vx0 && minY < vy1 && maxY > vy0;
    return !intersects;
  }, [bboxKey, sizeKey, x, y, zoom]);

  if (!offContent) return null;

  const backToContent = () => {
    const { nodeLookup, width, height, transform } = store.getState();
    const [tx, ty, tz] = transform;
    if (!tz) return;
    // Middle of the view in flow coordinates.
    const cx = (width / 2 - tx) / tz;
    const cy = (height / 2 - ty) / tz;
    let nearestId: string | null = null;
    let nearest = Infinity;
    for (const n of nodeLookup.values()) {
      const pos = n.internals?.positionAbsolute ?? n.position;
      const w = n.measured?.width ?? 0;
      const h = n.measured?.height ?? 0;
      // Distance from the view's middle to the node's rect (0 when inside).
      const dx = Math.max(pos.x - cx, 0, cx - (pos.x + w));
      const dy = Math.max(pos.y - cy, 0, cy - (pos.y + h));
      const d = dx * dx + dy * dy;
      if (d < nearest) {
        nearest = d;
        nearestId = n.id;
      }
    }
    if (!nearestId) return;
    window.dispatchEvent(
      new CustomEvent('ws:focus-item', { detail: { itemId: nearestId } }),
    );
  };

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center">
      <button
        type="button"
        onClick={backToContent}
        className="liquid-glass pointer-events-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium text-foreground/80 shadow-lg transition-colors hover:text-foreground"
      >
        <LocateFixed className="h-3.5 w-3.5" strokeWidth={1.8} />
        {t('railControls.backToContent')}
      </button>
    </div>
  );
}
